import React, { useState } from 'react';

// === EXPANDED DUMMY DATA ===
const servicesData = {
  hair: [
    { name: 'Advanced Haircut & Wash', price: '₹500', desc: 'Includes styling and blowdry.' },
    { name: 'Keratin Treatment', price: '₹2500+', desc: 'Frizz-free hair for up to 3 months.' },
    { name: 'Balayage / Highlights', price: '₹3000+', desc: 'Premium colors from L\'Oréal.' },
    { name: 'Hair Spa (Deep Nourish)', price: '₹1200', desc: 'Relaxing massage with steaming.' }
  ],
  skin: [
    { name: 'Hydra Facial', price: '₹1500', desc: 'Deep cleaning and glowing skin.' },
    { name: 'O3+ Skin Whitening', price: '₹1200', desc: 'Removes tan and evens skin tone.' },
    { name: 'Full Body Spa', price: '₹2000', desc: 'Swedish massage for full relaxation.' },
    { name: 'Premium Waxing', price: '₹800', desc: 'Painless RICA wax for full body.' }
  ],
  makeup: [
    { name: 'Bridal HD Makeup', price: '₹15,000', desc: 'Complete bridal look with draping.' },
    { name: 'Airbrush Party Makeup', price: '₹3,500', desc: 'Flawless finish for evening events.' },
    { name: 'Pre-Wedding Styling', price: '₹5,000', desc: '2 distinct looks for photoshoots.' },
    { name: 'Groom Makeup', price: '₹4,000', desc: 'Subtle touchups and hair styling.' }
  ]
};

const galleryPhotos = [
  'https://images.unsplash.com/photo-1487412947147-5cebf100ffc2?q=80&w=600&auto=format&fit=crop',
  'https://images.unsplash.com/photo-1560066984-138dadb4c035?q=80&w=600&auto=format&fit=crop',
  'https://images.unsplash.com/photo-1522337660859-02fbefca4702?q=80&w=600&auto=format&fit=crop',
  'https://images.unsplash.com/photo-1596178060671-7a80dc8059ea?q=80&w=600&auto=format&fit=crop',
  'https://images.unsplash.com/photo-1516975080661-46bfa99281a8?q=80&w=600&auto=format&fit=crop',
  'https://images.unsplash.com/photo-1512290923902-8a9f81dc236c?q=80&w=600&auto=format&fit=crop',
  'https://images.unsplash.com/photo-1616394584738-fc6e612e71b9?q=80&w=600&auto=format&fit=crop',
  'https://images.unsplash.com/photo-1595152772835-219674b2a8a6?q=80&w=600&auto=format&fit=crop',
  'https://images.unsplash.com/photo-1521590832167-7bcbfaa6381f?q=80&w=600&auto=format&fit=crop',
];

const reviews = [
  { name: "Priya Sharma", text: "Best bridal makeup ever! The team was highly professional and made my special day perfect.", rating: "⭐⭐⭐⭐⭐" },
  { name: "Anjali Gupta", text: "Loved their Keratin treatment. My hair feels so soft and the ambiance is very relaxing.", rating: "⭐⭐⭐⭐⭐" },
  { name: "Neha Verma", text: "Got a hydra facial done here. The staff is very polite and the glow is unreal!", rating: "⭐⭐⭐⭐" }
];

// === MAIN COMPONENT ===
const SalonMidPage: React.FC = () => {
  const [currentPage, setCurrentPage] = useState<'home' | 'services' | 'gallery' | 'contact'>('home');
  const [isFormSubmitted, setIsFormSubmitted] = useState(false);

  const handleContactSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsFormSubmitted(true);
    setTimeout(() => setIsFormSubmitted(false), 4000);
  };

  return (
    <div className="min-h-screen bg-[#FDFBF9] text-stone-800 font-sans flex flex-col">
      
      {/* --- NAVBAR --- */}
      <nav className="bg-white sticky top-0 z-50 shadow-sm border-b border-rose-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2 cursor-pointer" onClick={() => setCurrentPage('home')}>
              <span className="text-3xl text-rose-500">🌸</span>
              <span className="text-2xl font-serif font-bold text-stone-900">
                Blush & <span className="text-rose-600 font-light">Glow</span>
              </span>
            </div>
            
            <div className="flex items-center gap-2 sm:gap-6 overflow-x-auto w-full md:w-auto pb-2 md:pb-0">
              {['home', 'services', 'gallery', 'contact'].map((page) => (
                <button
                  key={page}
                  onClick={() => {
                    setCurrentPage(page as any);
                    window.scrollTo(0, 0); // Reset scroll position on page change
                  }}
                  className={`capitalize text-sm font-semibold tracking-wider px-4 py-2 rounded-full transition-all whitespace-nowrap ${
                    currentPage === page 
                      ? 'bg-rose-100 text-rose-800 shadow-inner' 
                      : 'text-stone-500 hover:text-rose-600 hover:bg-rose-50'
                  }`}
                >
                  {page}
                </button>
              ))}
            </div>
          </div>
        </div>
      </nav>

      {/* --- PAGE CONTENT AREA --- */}
      <main className="flex-grow animate-fadeIn">
        
        {/* ================= 1. HOME PAGE (Expanded) ================= */}
        {currentPage === 'home' && (
          <div className="animate-slideUp pb-20">
            {/* Hero Section */}
            <div className="max-w-7xl mx-auto px-6 py-16 md:py-24 grid md:grid-cols-2 gap-12 items-center">
              <div className="space-y-6">
                <span className="bg-rose-100 text-rose-800 px-3 py-1 rounded-full font-bold tracking-widest uppercase text-xs">Premium Salon Experience</span>
                <h1 className="text-5xl lg:text-7xl font-serif text-stone-900 leading-[1.1]">
                  Enhance Your <br/> <span className="italic text-rose-500">Natural Beauty</span>.
                </h1>
                <p className="text-stone-600 text-lg max-w-md leading-relaxed">
                  Your premier destination for luxury hair, skin, and bridal makeup services. Step in to relax, step out looking flawless.
                </p>
                <div className="flex gap-4 pt-4">
                  <button onClick={() => setCurrentPage('contact')} className="bg-stone-900 text-white px-8 py-4 rounded-full font-semibold hover:bg-rose-600 transition shadow-lg">
                    Book Appointment
                  </button>
                  <button onClick={() => setCurrentPage('services')} className="border border-stone-300 text-stone-800 px-8 py-4 rounded-full font-semibold hover:bg-stone-100 transition">
                    Our Menu
                  </button>
                </div>
              </div>
              <div className="relative h-[500px] lg:h-[600px] rounded-[2rem] overflow-hidden shadow-2xl">
                <img src="https://images.unsplash.com/photo-1522337360788-8b13dee7a37e?q=80&w=800&auto=format&fit=crop" alt="Salon Home" className="w-full h-full object-cover" />
              </div>
            </div>

            {/* Why Choose Us Section */}
            <div className="bg-rose-50/50 py-20 px-6 border-y border-rose-100">
              <div className="max-w-7xl mx-auto">
                <div className="text-center mb-16">
                  <h2 className="text-4xl font-serif text-stone-900">The Blush & Glow Difference</h2>
                  <p className="text-stone-500 mt-3">Why our clients keep coming back.</p>
                </div>
                <div className="grid md:grid-cols-3 gap-10">
                  <div className="bg-white p-8 rounded-2xl text-center shadow-sm hover:shadow-md transition">
                    <span className="text-5xl mb-4 block">🌿</span>
                    <h3 className="text-xl font-bold mb-2">Premium Products</h3>
                    <p className="text-stone-600">We use only organic, top-tier international brands for all your skin and hair treatments.</p>
                  </div>
                  <div className="bg-white p-8 rounded-2xl text-center shadow-sm hover:shadow-md transition">
                    <span className="text-5xl mb-4 block">✂️</span>
                    <h3 className="text-xl font-bold mb-2">Expert Stylists</h3>
                    <p className="text-stone-600">Our team consists of certified professionals with years of experience in modern styling.</p>
                  </div>
                  <div className="bg-white p-8 rounded-2xl text-center shadow-sm hover:shadow-md transition">
                    <span className="text-5xl mb-4 block">✨</span>
                    <h3 className="text-xl font-bold mb-2">Hygiene First</h3>
                    <p className="text-stone-600">We maintain strict sanitation protocols. Single-use kits for facial and skin treatments.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Reviews Section */}
            <div className="max-w-7xl mx-auto px-6 py-20">
              <div className="text-center mb-16">
                <h2 className="text-4xl font-serif text-stone-900">Client Stories</h2>
              </div>
              <div className="grid md:grid-cols-3 gap-8">
                {reviews.map((review, idx) => (
                  <div key={idx} className="bg-white p-8 rounded-3xl border border-stone-100 shadow-sm relative">
                    <div className="text-rose-400 mb-4">{review.rating}</div>
                    <p className="text-stone-700 italic mb-6">"{review.text}"</p>
                    <div className="border-t border-stone-100 pt-4 flex items-center gap-3">
                      <div className="w-10 h-10 bg-rose-100 rounded-full flex items-center justify-center font-bold text-rose-700">
                        {review.name.charAt(0)}
                      </div>
                      <span className="font-bold text-stone-900">{review.name}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ================= 2. SERVICES PAGE (Expanded) ================= */}
        {currentPage === 'services' && (
          <div className="animate-slideUp pb-24">
            <div className="bg-stone-900 text-white py-16 px-6 text-center">
              <h2 className="text-4xl md:text-5xl font-serif text-white mb-4">Our Curated Services</h2>
              <p className="text-stone-300 text-lg max-w-2xl mx-auto">Premium treatments designed to rejuvenate your body and elevate your style.</p>
            </div>

            <div className="max-w-7xl mx-auto px-6 py-16 grid lg:grid-cols-3 gap-10">
              {/* Hair Menu */}
              <div className="bg-white p-8 rounded-3xl shadow-sm border border-stone-100 hover:border-rose-200 transition h-fit">
                <div className="text-5xl mb-6">✂️</div>
                <h3 className="text-3xl font-serif mb-8 text-stone-900 border-b border-stone-100 pb-4">Hair Care</h3>
                <ul className="space-y-6">
                  {servicesData.hair.map((item, idx) => (
                    <li key={idx} className="flex justify-between items-start group">
                      <div>
                        <p className="text-stone-800 font-semibold group-hover:text-rose-600 transition">{item.name}</p>
                        <p className="text-xs text-stone-500 mt-1">{item.desc}</p>
                      </div>
                      <span className="font-bold text-rose-800 whitespace-nowrap ml-4">{item.price}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Skin Menu */}
              <div className="bg-white p-8 rounded-3xl shadow-sm border border-stone-100 hover:border-rose-200 transition h-fit">
                <div className="text-5xl mb-6">✨</div>
                <h3 className="text-3xl font-serif mb-8 text-stone-900 border-b border-stone-100 pb-4">Skin & Spa</h3>
                <ul className="space-y-6">
                  {servicesData.skin.map((item, idx) => (
                    <li key={idx} className="flex justify-between items-start group">
                      <div>
                        <p className="text-stone-800 font-semibold group-hover:text-rose-600 transition">{item.name}</p>
                        <p className="text-xs text-stone-500 mt-1">{item.desc}</p>
                      </div>
                      <span className="font-bold text-rose-800 whitespace-nowrap ml-4">{item.price}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Makeup Menu (Highlighted) */}
              <div className="bg-rose-900 p-8 rounded-3xl shadow-xl text-white h-fit relative overflow-hidden">
                <div className="absolute top-0 right-0 bg-rose-500 text-white text-xs font-bold px-4 py-1 rounded-bl-xl">POPULAR</div>
                <div className="text-5xl mb-6">💄</div>
                <h3 className="text-3xl font-serif mb-8 text-white border-b border-rose-700 pb-4">Bridal & Makeup</h3>
                <ul className="space-y-6">
                  {servicesData.makeup.map((item, idx) => (
                    <li key={idx} className="flex justify-between items-start">
                      <div>
                        <p className="text-rose-50 font-semibold">{item.name}</p>
                        <p className="text-xs text-rose-300 mt-1">{item.desc}</p>
                      </div>
                      <span className="font-bold text-white whitespace-nowrap ml-4">{item.price}</span>
                    </li>
                  ))}
                </ul>
                <button onClick={() => setCurrentPage('contact')} className="w-full mt-10 bg-white text-rose-900 font-bold py-3 rounded-xl hover:bg-rose-50 transition">
                  Book Makeup Slot
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ================= 3. GALLERY PAGE (Expanded) ================= */}
        {currentPage === 'gallery' && (
          <div className="max-w-7xl mx-auto px-6 py-16 animate-slideUp">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-serif text-stone-900">Our Masterpieces</h2>
              <p className="text-stone-500 mt-4 max-w-2xl mx-auto text-lg">
                Browse through our portfolio of recent transformations. From stunning bridal makeups to chic haircuts, see our experts at work.
              </p>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
              {galleryPhotos.map((url, idx) => (
                <div key={idx} className="aspect-[4/5] rounded-3xl overflow-hidden group shadow-md bg-stone-200 relative">
                  <img 
                    src={url} 
                    alt={`Salon Work ${idx}`} 
                    className="w-full h-full object-cover group-hover:scale-110 transition duration-700"
                  />
                  {/* Hover Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-stone-900/80 via-stone-900/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-6">
                    <span className="text-white font-semibold">View Details &rarr;</span>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-16 text-center bg-rose-50 p-12 rounded-3xl border border-rose-100">
              <h3 className="text-2xl font-serif mb-4">Want to see more of our daily work?</h3>
              <p className="text-stone-600 mb-6">Follow us on Instagram for daily updates, offers, and beauty tips.</p>
              <button className="bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500 text-white font-bold py-3 px-8 rounded-full shadow-lg hover:opacity-90 transition">
                @BlushAndGlow_Official
              </button>
            </div>
          </div>
        )}

        {/* ================= 4. CONTACT PAGE (Expanded) ================= */}
        {currentPage === 'contact' && (
          <div className="max-w-7xl mx-auto px-6 py-16 animate-slideUp">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-serif text-stone-900">Get In Touch</h2>
              <p className="text-stone-500 mt-4 text-lg">Book an appointment or drop by our studio for a free consultation.</p>
            </div>
            
            <div className="grid lg:grid-cols-12 gap-12 bg-white p-6 md:p-12 rounded-[2.5rem] shadow-xl border border-stone-100">
              
              {/* Form Side */}
              <div className="lg:col-span-5">
                <h3 className="text-3xl font-serif mb-8 text-stone-900">Send an Enquiry</h3>
                {isFormSubmitted ? (
                  <div className="bg-green-50 border border-green-200 text-green-700 p-8 rounded-3xl text-center h-[350px] flex flex-col justify-center">
                    <span className="text-6xl block mb-4">✅</span>
                    <h4 className="font-bold text-2xl mb-2">Message Sent!</h4>
                    <p>Thank you! Our front desk will call you back within 2 working hours to confirm your slot.</p>
                    <button onClick={() => setIsFormSubmitted(false)} className="mt-8 text-sm font-semibold underline text-green-800">Send another message</button>
                  </div>
                ) : (
                  <form onSubmit={handleContactSubmit} className="space-y-5">
                    <div>
                      <label className="block text-sm font-bold text-stone-600 mb-2 uppercase tracking-wide">Full Name</label>
                      <input required type="text" className="w-full p-4 rounded-xl border border-stone-200 focus:outline-none focus:border-rose-500 focus:ring-1 focus:ring-rose-500 bg-stone-50 transition" placeholder="Jane Doe" />
                    </div>
                    <div>
                      <label className="block text-sm font-bold text-stone-600 mb-2 uppercase tracking-wide">Phone Number</label>
                      <input required type="tel" className="w-full p-4 rounded-xl border border-stone-200 focus:outline-none focus:border-rose-500 focus:ring-1 focus:ring-rose-500 bg-stone-50 transition" placeholder="+91 00000 00000" />
                    </div>
                    <div>
                      <label className="block text-sm font-bold text-stone-600 mb-2 uppercase tracking-wide">Interested In</label>
                      <select className="w-full p-4 rounded-xl border border-stone-200 focus:outline-none focus:border-rose-500 focus:ring-1 focus:ring-rose-500 bg-stone-50 transition">
                        <option>Hair Cut / Styling</option>
                        <option>Skin Care / Facial</option>
                        <option>Bridal Makeup Package</option>
                        <option>General Enquiry</option>
                      </select>
                    </div>
                    <button type="submit" className="w-full bg-stone-900 text-white font-bold py-4 rounded-xl hover:bg-rose-600 transition shadow-lg mt-4">
                      Request Callback
                    </button>
                  </form>
                )}
              </div>

              {/* Details & Map Side */}
              <div className="lg:col-span-7 space-y-8 flex flex-col">
                <div className="grid sm:grid-cols-2 gap-6">
                  <div className="bg-rose-50 p-6 rounded-2xl border border-rose-100">
                    <span className="text-3xl mb-3 block">📍</span>
                    <h4 className="font-bold text-stone-900 text-lg mb-2">Studio Location</h4>
                    <p className="text-stone-600 leading-relaxed">
                      Shop No. 12, Elite Arcade,<br/>
                      Near Fashion Street Market,<br/>
                      Delhi NCR, India - 110001
                    </p>
                  </div>
                  <div className="bg-stone-50 p-6 rounded-2xl border border-stone-100">
                    <span className="text-3xl mb-3 block">🕒</span>
                    <h4 className="font-bold text-stone-900 text-lg mb-2">Working Hours</h4>
                    <p className="text-stone-600 leading-relaxed">
                      Mon - Sat: 10:00 AM - 8:00 PM<br/>
                      Sunday: 11:00 AM - 5:00 PM<br/>
                      <span className="text-rose-600 font-semibold mt-2 block">Call: +91 98765 43210</span>
                    </p>
                  </div>
                </div>
                
                {/* Embedded Map */}
                <div className="flex-grow min-h-[300px] rounded-3xl overflow-hidden border-4 border-stone-100 relative group">
                  <iframe 
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d14008.114827184043!2d77.21595625!3d28.6295706!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390cfd37b741d057%3A0xc46188cbcc01452!2sConnaught%20Place%2C%20New%20Delhi%2C%20Delhi%20110001!5e0!3m2!1sen!2sin!4v1700000000000!5m2!1sen!2sin" 
                    width="100%" 
                    height="100%" 
                    style={{ border: 0 }} 
                    allowFullScreen={false} 
                    loading="lazy"
                    className="grayscale group-hover:grayscale-0 transition duration-500"
                  ></iframe>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* ================= EXTENDED FOOTER ================= */}
      <footer className="bg-stone-950 text-stone-300 py-16 border-t-4 border-rose-600 mt-auto">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-12">
          
          {/* Col 1: Brand Info */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <span className="text-3xl text-rose-500">🌸</span>
              <span className="text-2xl font-serif font-bold text-white">
                Blush & <span className="text-rose-400 font-light">Glow</span>
              </span>
            </div>
            <p className="text-stone-400 text-sm leading-relaxed">
              Your neighborhood luxury salon dedicated to bringing out the best version of you with premium products and expert care.
            </p>
          </div>

          {/* Col 2: Quick Links */}
          <div>
            <h4 className="text-white font-bold uppercase tracking-widest text-sm mb-6">Quick Links</h4>
            <ul className="space-y-3 text-sm">
              <li><button onClick={() => {setCurrentPage('home'); window.scrollTo(0,0);}} className="hover:text-rose-400 transition">Home</button></li>
              <li><button onClick={() => {setCurrentPage('services'); window.scrollTo(0,0);}} className="hover:text-rose-400 transition">Our Services</button></li>
              <li><button onClick={() => {setCurrentPage('gallery'); window.scrollTo(0,0);}} className="hover:text-rose-400 transition">Portfolio</button></li>
              <li><button onClick={() => {setCurrentPage('contact'); window.scrollTo(0,0);}} className="hover:text-rose-400 transition">Contact Us</button></li>
            </ul>
          </div>

          {/* Col 3: Services Summary */}
          <div>
            <h4 className="text-white font-bold uppercase tracking-widest text-sm mb-6">Popular Services</h4>
            <ul className="space-y-3 text-sm">
              <li className="text-stone-400">Bridal HD Makeup</li>
              <li className="text-stone-400">Keratin Hair Spa</li>
              <li className="text-stone-400">Hydra Facial Glow</li>
              <li className="text-stone-400">Global Hair Coloring</li>
            </ul>
          </div>

          {/* Col 4: Newsletter/Social */}
          <div>
            <h4 className="text-white font-bold uppercase tracking-widest text-sm mb-6">Connect With Us</h4>
            <div className="flex gap-4 mb-6">
              <a href="#" className="w-10 h-10 rounded-full bg-stone-800 flex items-center justify-center hover:bg-rose-600 transition text-white">IG</a>
              <a href="#" className="w-10 h-10 rounded-full bg-stone-800 flex items-center justify-center hover:bg-rose-600 transition text-white">FB</a>
              <a href="#" className="w-10 h-10 rounded-full bg-stone-800 flex items-center justify-center hover:bg-rose-600 transition text-white">YT</a>
            </div>
            <p className="text-xs text-stone-500">For franchise enquiries, email us at:<br/><span className="text-stone-300">hello@blushandglow.com</span></p>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-6 mt-16 pt-8 border-t border-stone-800 text-center text-sm text-stone-500 flex flex-col md:flex-row justify-between items-center gap-4">
          <p>© {new Date().getFullYear()} Blush & Glow Studio. All rights reserved.</p>
          <p>Designed as a Portfolio Demo.</p>
        </div>
      </footer>

      {/* Animations */}
      <style dangerouslySetInnerHTML={{ __html: `
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        .animate-slideUp { animation: slideUp 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
        .animate-fadeIn { animation: fadeIn 0.4s ease-out forwards; }
      `}} />
    </div>
  );
};

export default SalonMidPage;